import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "shared/useServerPaginatedList";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Option } from "../../interface/Option";
import {
  Result,
  relationAddAgeAndGender,
} from "../../interface/RelationAddAgeAndGender";
import { RelationAddAgeAndGenderAxiosInstance } from "../../Network/RelationAddAgeAndGenderAxiosInstance";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import ListofCustomIcd from "../Shared/ListofcustemIcd";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import ListOfDoctorName from "../Shared/Doctor";
import PolicyNames from "../Shared/PolicyNames";
import FileUpload from "Pages/uploadFile/UploadFileIcdAgeAndGender/UploadFileIcdAgeAndGender";
import Vip from "../Shared/Vip";
import ListOfSpecialty from "../Shared/Specialty";


export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── preserveScroll helper ────────────────────────────────────────────────────
const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => {
    if (scrollable) scrollable.scrollTop = scrollTop;
  });
};

// ─── mode conversion helpers ──────────────────────────────────────────────────
const modeToCard = (mode: string | undefined): { cardMode: "EXCLUSION" | "INCLUSION" | null; isActive: boolean } => {
  if (mode === "EXCLUDED") return { cardMode: "EXCLUSION", isActive: true };
  if (mode === "INCLUDED") return { cardMode: "INCLUSION", isActive: true };
  return { cardMode: null, isActive: false };
};

const cardToMode = (cardMode: "EXCLUSION" | "INCLUSION" | null): string => {
  if (cardMode === "EXCLUSION") return "EXCLUDED";
  if (cardMode === "INCLUSION") return "INCLUDED";
  return "DISABLED";
};

// ─── Helper Components ────────────────────────────────────────────────────────
const TagsWrap: React.FC<{ items: string[]; color: "blue" | "green" | "purple" | "gray" }> = ({ items, color }) => {
  const styles: Record<string, React.CSSProperties> = {
    blue:   { background: "#eff6ff", color: "#1d4ed8", border: "0.5px solid #bfdbfe" },
    green:  { background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" },
    purple: { background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" },
    gray:   { background: "#f8fafc", color: "#475569", border: "0.5px solid #e2e8f0" },
  };
  if (!items.length) return <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
      {items.map((item, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...styles[color] }}>{item}</span>
      ))}
    </div>
  );
};

// ─── EntityCard ─────────────────────────────────────────────────────────────
const entityColors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200" },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200" },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200" },
};

interface EntityCardProps {
  label: string;
  icon: string;
  color: string;
  isActive: boolean;
  mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void;
  onInclusion: () => void;
  onClear: () => void;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({
  label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, children,
}) => {
  const c = entityColors[color] ?? entityColors.slate;

  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => {
      if (scrollable) scrollable.scrollTop = scrollTop;
    });
  };

  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button
          type="button"
          onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10"
          title="Clear"
        >
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button
            type="button"
            onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclusion
          </button>
          <button
            type="button"
            onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Inclusion
          </button>
        </div>
      </div>
      {isActive && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ─── StatusToggle ─────────────────────────────────────────────────────────────
const StatusToggle: React.FC<{ label: string; dotColor: string; value: boolean; onChange: (v: boolean) => void }> = ({ label, dotColor, value, onChange }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
    <div className="flex items-center gap-2 mb-2">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
      <span className="text-xs font-medium text-gray-600">{label}</span>
    </div>
    <div className="flex gap-2">
      <button type="button" onClick={() => onChange(true)}
        className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${value ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
        Active
      </button>
      <button type="button" onClick={() => onChange(false)}
        className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${!value ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
        Inactive
      </button>
    </div>
  </div>
);
// ─── Main Component ───────────────────────────────────────────────────────────
const IcdAddAgeAndGender: React.FC = () => {
  const [items, setItems] = useState<relationAddAgeAndGender>({ result: [] });
  const userRole = getUserRole();
const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

const [forceDialogOpen, setForceDialogOpen] = useState(false);
const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
const [forceId, setForceId] = useState<string | number | null>(null);
const [forceError, setForceError] = useState<string>("");

  const [gender, setGender] = useState<string>("");
  const [customGroupName, setCustomGroupName] = useState<string>("");
  const [icds, setIcds] = useState<Option[]>([]);
  const [IcdDescription, setIcdDescription] = useState<string>("");
  const [customIcdGroups, setCustomIcdGroups] = useState<Option[]>([]);
  const [medicalIcdGroups, setMedicalIcdGroups] = useState<Option[]>([]);
  const [lowerAge, setLowerAge] = useState<number | null>(null);
  const [higherAge, setHigherAge] = useState<number | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isActiveToAge, setIsActiveToAge] = useState<boolean>(true);
  const [isActiveToGender, setIsActiveToGender] = useState<boolean>(true);

  // ── Provider ──
  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  // ── Doctor ──
  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);

  // ── Specialty ──
  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);

  // ── Networks ──
  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);

  // ── Insurance Plan ──
  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);

  // ── Payer ──
  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  // ── Policy ──
  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);

  // ── Patients ──
  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);

  // ── VIP ──
  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);

  // ── User Level ──
  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  // ── Entities toggle ──
  const [showEntities, setShowEntities] = useState(false);

  // clear flags
  const [clearIcds, setClearIcds] = useState(false);
  const [clearCustomIcdGroups, setClearCustomIcdGroups] = useState(false);
  const [clearMedicalIcdGroups, setClearMedicalIcdGroups] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);
  const [clearVip, setClearVip] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);

  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const selectedItemRef = useRef<Result | null>(null);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  const [icdCodesDialogOpen, setIcdCodesDialogOpen] = useState(false);
  const [icdCodesDialogList, setIcdCodesDialogList] = useState<Array<{ label: string; kind: "icd" | "medical" | "custom" }>>([]);
  const [icdCodesDialogTitle, setIcdCodesDialogTitle] = useState("");

  const [filterSearch, setFilterSearch] = useState<string>("");

  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterGender, setFilterGender] = useState<string>("");
  const [filterGenderValidation, setFilterGenderValidation] = useState<string>("");
  const [filterAge, setFilterAge] = useState<string>("");
  const [filterAgeValidation, setFilterAgeValidation] = useState<string>("");

  const [genderOpen, setGenderOpen] = useState(false);
  const [gValidOpen, setGValidOpen] = useState(false);
  const [aValidOpen, setAValidOpen] = useState(false);

  const genderRef = useRef<HTMLDivElement>(null);
  const gValidRef = useRef<HTMLDivElement>(null);
  const aValidRef = useRef<HTMLDivElement>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  // ─── Reset all entity states ──────────────────────────────────────────────
  const resetAllEntityStates = () => {
    setProvider([]);      setProviderMode(null);  setIsProviderActive(false);  setSelectAllProviders(false);
    setDoctor([]);        setDoctorMode(null);    setIsDoctorActive(false);    setSelectAllDoctor(false);
    setSpecialty([]);     setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
    setNetworks([]);      setNetworkMode(null);   setIsNetworkActive(false);   setSelectAllNetworks(false);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]);         setPayerMode(null);     setIsPayerActive(false);     setSelectAllPayer(false);
    setPolicy([]);        setPolicyMode(null);    setIsPolicyActive(false);    setSelectAllPolicy(false);
    setPatients([]);      setPatientMode(null);   setIsPatientActive(false);   setSelectAllPatients(false);
    setVip([]);           setVipMode(null);       setIsVipActive(false);       setSelectAllVip(false);
    setUserLevel([]);     setUserMode(null);      setIsUserActive(false);      setSelectAllUser(false);
    setClearProvider(true); setClearNetworks(true); setClearInsuranceplan(true);
    setClearPatient(true);  setClearPolicy(true);   setClearDoctor(true);
    setClearVip(true);      setClearSpecialty(true);
  };

  const clear = () => {
    setGender(""); setCustomGroupName(""); setIcds([]); setCustomIcdGroups([]);
    setMedicalIcdGroups([]); setLowerAge(null); setHigherAge(null);
    setIsActiveToAge(true); setIsActiveToGender(true); setIsActive(true);
    setSelectedMainOption(null); setShowEntities(false);
    resetAllEntityStates();
  };

  // ─── Validate Entities ────────────────────────────────────────────────────
  const validateEntities = (): string | null => {
    const checks = [
      { isActive: isProviderActive,   hasData: selectAllProviders  || provider.length > 0,     label: "Providers" },
      { isActive: isDoctorActive,     hasData: selectAllDoctor     || doctor.length > 0,        label: "Doctor" },
      { isActive: isSpecialtyActive,  hasData: selectAllSpecialty  || specialty.length > 0,     label: "Specialty" },
      { isActive: isNetworkActive,    hasData: selectAllNetworks   || networks.length > 0,      label: "Networks" },
      { isActive: isInsuranceActive,  hasData: selectAllInsurance  || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,     hasData: selectAllPolicy     || policy.length > 0,        label: "Policy" },
      { isActive: isPatientActive,    hasData: selectAllPatients   || patients.length > 0,      label: "Patients" },
      { isActive: isVipActive,        hasData: selectAllVip        || vip.length > 0,           label: "VIP" },
      { isActive: isPayerActive,      hasData: selectAllPayer      || Payer.length > 0,         label: "Payer" },
      { isActive: isUserActive,       hasData: selectAllUser       || userLevel.length > 0,     label: "User Level" },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length >= 1) {
      return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the ✕ button.`;
    }
    return null;
  };

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  // ─── Server-side pagination and filters ───────────────────────────────────
  const listFilters = {
    search: filterSearch.trim(),
    is_deleted: deletedFilter,
    gender: filterGender,
    age: filterAge,
    genderValidation: filterGenderValidation,
    ageValidation: filterAgeValidation,
  };

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Result, typeof listFilters>({
    filters: listFilters,
    fetchPage: ({ page, limit, filters }) =>
      RelationAddAgeAndGenderAxiosInstance.get("getage_gender", {
        params: {
          page,
          limit,
          relation_name: filters.search || undefined,
          disease: filters.search || undefined,
          gender: filters.gender || undefined,
          age: filters.age.trim() || undefined,
          is_active_to_gender:
            filters.genderValidation === "true" ||
            filters.genderValidation === "false"
              ? filters.genderValidation
              : undefined,
          is_active_to_age:
            filters.ageValidation === "true" ||
            filters.ageValidation === "false"
              ? filters.ageValidation
              : undefined,
          is_deleted: filters.is_deleted,
        },
      }),
    setRows: (rows) => {
      setItems({ result: rows });
      if (selectedItemRef.current) {
        const updated = rows.find(
          (row) => row.id === selectedItemRef.current!.id,
        );
        if (updated) {
          setSelectedItem(updated);
          selectedItemRef.current = updated;
        }
      }
    },
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id || 0) - Number(a.id || 0))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1,
        ),
    onError: () =>
      toast({
        title: "Error",
        description: "Failed to fetch Relation ICD Add Age And Gender",
        variant: "destructive",
      }),
  });

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!genderRef.current?.contains(e.target as Node)) setGenderOpen(false);
      if (!gValidRef.current?.contains(e.target as Node)) setGValidOpen(false);
      if (!aValidRef.current?.contains(e.target as Node)) setAValidOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredData = items.result;

  const handleOpenDialog = (item: Result) => {
    setSelectedItem(item); selectedItemRef.current = item; setIsViewDialogOpen(true);
  };
const handleCloseDialog = () => {
  setIsViewDialogOpen(false); setSelectedItem(null); selectedItemRef.current = null; setShowAllUpdates(false);
};
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = (item: Result) => { handleEdit(item); setIsDialogOpen(true); };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

const removeItem = async (id: string | number): Promise<void> => {
  try {
    const response = await RelationAddAgeAndGenderAxiosInstance.delete(`softDeleteage-gender/${id}`, { data: { token } });
    if (response.data.success) {
      await fetchItems();
      toast({ title: "Success", description: "Record Deleted Successfully." });
    } else {
      setForceId(id); setForceAction("softDelete");
      setForceError(response.data.error || "This record is used elsewhere.");
      setForceDialogOpen(true);
    }
  } catch (error: any) {
    const errMsg = error.response?.data?.error || "Failed to delete";
    if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
      setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
    } else {
      toast({ title: "Error", description: errMsg, variant: "destructive" });
    }
  }
};

const restoreItem = async (id: string | number): Promise<void> => {
  try {
    const response = await RelationAddAgeAndGenderAxiosInstance.patch(`restoreage-gender/${id}`, { token });
    if (response.data.success) {
      await fetchItems();
      toast({ title: "Success", description: "Record restored successfully." });
    } else {
      toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
    }
  } catch (error: any) {
    toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
  }
};

const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
  try {
    const url = force ? `deleteage-gender/${id}?force=true` : `deleteage-gender/${id}`;
    const response = await RelationAddAgeAndGenderAxiosInstance.delete(url, { data: { token } });
    if (response.data.success) {
      await fetchItems();
      setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
      toast({ title: "Success", description: "Record Permanently Deleted." });
    } else {
      setForceAction("hardDelete");
      setForceError(response.data.error || "This record is used elsewhere.");
    }
  } catch (error: any) {
    setForceAction("hardDelete");
    setForceError(error.response?.data?.error || "Failed to delete");
  }
};

const forceSoftDelete = async (id: string | number): Promise<void> => {
  try {
    const response = await RelationAddAgeAndGenderAxiosInstance.delete(`softDeleteage-gender/${id}?force=true`, { data: { token } });
    if (response.data.success) {
      await fetchItems();
      setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
      toast({ title: "Success", description: "Record force Deleted Successfully." });
    } else {
      setForceError(response.data.error || "Failed to force delete");
    }
  } catch (error: any) {
    setForceError(error.response?.data?.error || "Failed to force delete");
  }
};

  // ─── Build helpers ────────────────────────────────────────────────────────
  const buildProviderValues = (providerArr: Option[]) =>
    providerArr.map((o: any) => {
      if (o.Provider_id !== undefined && o.Provider_name !== undefined)
        return { Provider_id: o.Provider_id, Provider_name: o.Provider_name };
      const match = o.label?.match(/^\((.+?)\)\s(.+)$/);
      if (match) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
      return { Provider_id: o.value?.toString() || "", Provider_name: o.label?.toString() || "" };
    });

  const buildDoctorData = (o: any) => {
    if (o.Doctor_id !== undefined && o.Doctor_name !== undefined)
      return { Doctor_id: o.Doctor_id, Doctor_name: o.Doctor_name };
    const match = o.label?.match(/^\((.+?)\)\s(.+)$/);
    if (match) return { Doctor_id: match[1].trim(), Doctor_name: match[2].trim() };
    return { Doctor_id: o.value?.toString() || "", Doctor_name: o.label?.toString() || "" };
  };

  const buildPolicyData = (policyArr: PolicyItem[]) =>
    policyArr.map((p: any) => {
      if (typeof p === "object" && p.Policy_id && !p.value) return { id: p.id || 0, Policy_id: p.Policy_id, Policy_name: p.Policy_name, plans: p.plans || [] };
      if (typeof p === "object" && p.value) return { id: p.value.id || 0, Policy_id: p.value.Policy_id || "", Policy_name: p.value.Policy_name || "", plans: p.value.plans || [] };
      if (typeof p === "string") return { id: 0, Policy_id: p, Policy_name: p, plans: [] };
      return { id: 0, Policy_id: "", Policy_name: "", plans: [] };
    });

  const buildSpecialtyData = (specialtyArr: Option[]) =>
    specialtyArr.map((o: any) => {
      if (o.id !== undefined && o.name !== undefined) return { id: o.id, name: o.name };
      const match = o.label?.match(/^\((.+?)\)\s(.+)$/);
      if (match) return { id: Number(match[1].trim()), name: match[2].trim() };
      return { id: Number(o.value) || 0, name: o.label?.toString() || "" };
    });

  // ─── Build toggle payload helper ─────────────────────────────────────────
  const buildTogglePayload = (item: Result) => {
    const networkSource = (item as any).network ?? item.networks ?? [];
    const patientSource = (item as any).patient ?? item.patients ?? [];
    return {
      gender: item.gender,
      relation_name: item.relation_name,
      icd: item.icd || [],
      custom_icd_groups: (item.custom_icd_groups || []).map((g: any) => g.id ?? g),
      medical_icd_groups: (item.medical_icd_groups || []).map((g: any) => g.id ?? g),
      lower_age: item.lower_age,
      higher_age: item.higher_age,
      age_allowed: true,
      provider: (item.provider || []).map((o: any) => ({ Provider_id: o.Provider_id || "", Provider_name: o.Provider_name || "" })),
      provider_mode: item.provider_mode ?? "DISABLED",
      provider_all: item.provider_all ?? false,
      network: (Array.isArray(networkSource) ? networkSource : []).map((n: any) => n.id ?? n),
      network_mode: item.network_mode ?? "DISABLED",
      network_all: item.network_all ?? false,
      insuranceplan: (item.insuranceplan || []).map((i: any) => i.id ?? i),
      insuranceplan_mode: item.insuranceplan_mode ?? "DISABLED",
      insuranceplan_all: item.insuranceplan_all ?? false,
      payer: item.payer || [],
      payer_mode: item.payer_mode ?? "DISABLED",
      payer_all: item.payer_all ?? false,
      patient: (Array.isArray(patientSource) ? patientSource : []).map((p: any) => p.name ?? p),
      patient_mode: item.patient_mode ?? "DISABLED",
      patient_all: item.patient_all ?? false,
      policy: (item.policy || []).map((p: any) => ({ id: p.id || 0, Policy_id: p.Policy_id || "", Policy_name: p.Policy_name || "", plans: p.plans || [] })),
      policy_mode: item.policy_mode ?? "DISABLED",
      policy_all: item.policy_all ?? false,
      user_level: item.user_level || [],
      user_mode: item.user_mode ?? "DISABLED",
      user_all: item.user_all ?? false,
      vip: (item.vip || []).map((v: any) => v.id ?? v),
      vip_mode: item.vip_mode ?? "DISABLED",
      vip_all: item.vip_all ?? false,
      doctor: (item.doctor || []).map((d: any) => ({ Doctor_id: d.Doctor_id ?? "", Doctor_name: d.Doctor_name ?? d })),
      doctor_mode: item.doctor_mode ?? "DISABLED",
      doctor_all: item.doctor_all ?? false,
      specialty: (item.specialty || []).map((s: any) => ({ id: s.id ?? 0, name: s.name ?? s })),
      specialty_mode: item.specialty_mode ?? "DISABLED",
      specialty_all: item.specialty_all ?? false,
      is_active_to_age: item.is_active_to_age,
      is_active_to_gender: item.is_active_to_gender,
      is_active: item.is_active !== false,
      id: item.id,
      token,
    };
  };

  const toggleActiveStatus = async (item: Result, field: "age" | "gender") => {
    try {
      const payload = {
        ...buildTogglePayload(item),
        is_active_to_age: field === "age" ? !item.is_active_to_age : item.is_active_to_age,
        is_active_to_gender: field === "gender" ? !item.is_active_to_gender : item.is_active_to_gender,
      };
      const response = await RelationAddAgeAndGenderAxiosInstance.put("updateage-gender", payload);
      if (response.data.success) { fetchItems(); toast({ title: "Success", description: "Status updated successfully." }); }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const toggleRelationStatus = async (item: Result) => {
    try {
      const payload = { ...buildTogglePayload(item), is_active: !item.is_active };
      const response = await RelationAddAgeAndGenderAxiosInstance.put("updateage-gender", payload);
      if (response.data.success) { fetchItems(); toast({ title: "Success", description: "Relation status updated successfully." }); }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };
  const addItem = async (): Promise<void> => {
    if (!customGroupName.trim()) { toast({ title: "Error", description: "Group Name is required and cannot be empty.", variant: "destructive" }); return; }
    if (customGroupName.trim().length < 3) { toast({ title: "Error", description: "Group Name must be at least 3 characters long.", variant: "destructive" }); return; }

    if (!isActiveToGender && !isActiveToAge) {
      toast({
        title: "Error",
        description: "At least one of Gender Status or Age Status must be Active. You cannot create a rule with both inactive.",
        variant: "destructive",
      });
      return;
    }

  if (isActiveToGender && !gender) {
      toast({
        title: "Error",
        description: "Gender Status is active — please choose a gender (Male or Female). No default is allowed.",
        variant: "destructive",
      });
      return;
    }

    if (isActiveToAge && (lowerAge == null || higherAge == null)) {
      toast({
        title: "Error",
        description: "Age Status is active — please enter both a lower and a higher age.",
        variant: "destructive",
      });
      return;
    }

    if (lowerAge != null && higherAge != null && Number(lowerAge) > Number(higherAge)) {
      toast({
        title: "Error",
        description: "Lower age cannot be greater than higher age.",
        variant: "destructive",
      });
      return;
    }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const newGroupArray = {
        gender, relation_name: customGroupName,
        icd: icds.map((o) => o.value),
        custom_icd_groups: customIcdGroups.map((o) => o.value),
        medical_icd_groups: medicalIcdGroups.map((o) => o.value),
        lower_age: lowerAge, higher_age: higherAge, age_allowed: true,
        provider: selectAllProviders ? [] : buildProviderValues(provider),
        provider_mode: cardToMode(providerMode),
        provider_all: selectAllProviders,
        network: selectAllNetworks ? [] : networks.map((o) => o.value),
        network_mode: cardToMode(networkMode),
        network_all: selectAllNetworks,
        insuranceplan: selectAllInsurance ? [] : insuranceplan.map((o) => o.value),
        insuranceplan_mode: cardToMode(insuranceMode),
        insuranceplan_all: selectAllInsurance,
        payer: selectAllPayer ? [] : Payer,
        payer_mode: cardToMode(payerMode),
        payer_all: selectAllPayer,
        patient: selectAllPatients ? [] : patients.map((o) => o.label),
        patient_mode: cardToMode(patientMode),
        patient_all: selectAllPatients,
        policy: selectAllPolicy ? [] : buildPolicyData(policy),
        policy_mode: cardToMode(policyMode),
        policy_all: selectAllPolicy,
        user_level: selectAllUser ? [] : userLevel,
        user_mode: cardToMode(userMode),
        user_all: selectAllUser,
        vip: selectAllVip ? [] : vip.map((o) => o.value),
        vip_mode: cardToMode(vipMode),
        vip_all: selectAllVip,
        doctor: selectAllDoctor ? [] : doctor.map(buildDoctorData),
        doctor_mode: cardToMode(doctorMode),
        doctor_all: selectAllDoctor,
        specialty: selectAllSpecialty ? [] : buildSpecialtyData(specialty),
        specialty_mode: cardToMode(specialtyMode),
        specialty_all: selectAllSpecialty,
        is_active_to_age: isActiveToAge,
        is_active_to_gender: isActiveToGender,
        is_active: isActive,
        token,
      };
      const response = await RelationAddAgeAndGenderAxiosInstance.post("addage-gender", newGroupArray);
      if (response.data.success) {
       await fetchItems();
  setCurrentPage(0);
  clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New Relation Add ICD Age And Gender Added Successfully." });
      } else {
        const errors = response.data.errors || [];
        toast({ title: "Error", description: errors.map((e: any) => e.error).join("\n") || response.data.message || "Failed to add the group.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Result) => {
    const mapToOptions = (arr: (string | number)[]): Option[] => arr.map((value) => ({ value, label: value.toString() }));

    // Networks — backend returns "network"
    const networkSource = (item as any).network ?? item.networks ?? [];
    const networksData = Array.isArray(networkSource)
      ? networkSource.map((obj: any) => typeof obj === "string" ? { id: 0, network_name: obj } : obj)
      : [];
    const mappedNetworks = networksData.map((o: any) => ({ value: o.id, label: o.id ? `(${o.id}) ${o.network_name}` : o.network_name }));
    setNetworks(mappedNetworks);
    const nCard = modeToCard(item.network_mode);
    setNetworkMode(nCard.cardMode); setIsNetworkActive(nCard.isActive || mappedNetworks.length > 0); setSelectAllNetworks(item.network_all ?? false);

    // Insurance Plan
    const insuranceplanData = Array.isArray(item.insuranceplan) ? item.insuranceplan.map((obj: any) => typeof obj === "string" ? { id: 0, Plan_name: obj } : obj) : [];
    const mappedInsurance = insuranceplanData.map((o: any) => ({ value: o.id, label: o.id ? `(${o.id}) ${o.Plan_name}` : o.Plan_name }));
    setInsuranceplan(mappedInsurance);
    const iCard = modeToCard(item.insuranceplan_mode);
    setInsuranceMode(iCard.cardMode); setIsInsuranceActive(iCard.isActive || mappedInsurance.length > 0); setSelectAllInsurance(item.insuranceplan_all ?? false);

    // Providers
    const ProvidersData = Array.isArray(item.provider) ? item.provider.map((obj: any) => typeof obj === "string" ? { Provider_id: 0, Provider_name: obj } : obj) : [];
    const mappedProviders = ProvidersData.map((o: any) => ({ value: o.Provider_id, label: o.Provider_id ? `(${o.Provider_id}) ${o.Provider_name}` : o.Provider_name }));
    setProvider(mappedProviders);
    const pCard = modeToCard(item.provider_mode);
    setProviderMode(pCard.cardMode); setIsProviderActive(pCard.isActive || mappedProviders.length > 0); setSelectAllProviders(item.provider_all ?? false);

    // Patients — backend returns "patient"
    const patientSource = (item as any).patient ?? item.patients ?? [];
    const PatientsData = Array.isArray(patientSource)
      ? patientSource.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj)
      : [];
    const mappedPatients = PatientsData.map((o: any) => ({ value: o.id, label: o.id ? `(${o.id}) ${o.name}` : o.name }));
    setPatients(mappedPatients);
    const patCard = modeToCard(item.patient_mode);
    setPatientMode(patCard.cardMode); setIsPatientActive(patCard.isActive || mappedPatients.length > 0); setSelectAllPatients(item.patient_all ?? false);

    // Policy
    const mappedPolicy = Array.isArray(item.policy) ? item.policy.map((obj: any) => ({ id: obj.id || 0, Policy_id: obj.Policy_id || "", Policy_name: obj.Policy_name || "", plans: obj.plans || [] })) : [];
    setPolicy(mappedPolicy);
    const polCard = modeToCard(item.policy_mode);
    setPolicyMode(polCard.cardMode); setIsPolicyActive(polCard.isActive || mappedPolicy.length > 0); setSelectAllPolicy(item.policy_all ?? false);

    // VIP
    const vipData = Array.isArray(item.vip) ? item.vip.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedVip = vipData.map((o: any) => ({ value: o.id, label: o.id ? `(${o.id}) ${o.name}` : o.name }));
    setVip(mappedVip);
    const vCard = modeToCard(item.vip_mode);
    setVipMode(vCard.cardMode); setIsVipActive(vCard.isActive || mappedVip.length > 0); setSelectAllVip(item.vip_all ?? false);

    // Doctor
    const DoctorsData = Array.isArray(item.doctor) ? item.doctor.map((obj: any) => typeof obj === "string" ? { Doctor_id: 0, Doctor_name: obj } : obj) : [];
    const mappedDoctors = DoctorsData.map((o: any) => ({ value: o.Doctor_id, label: o.Doctor_id ? `(${o.Doctor_id}) ${o.Doctor_name}` : o.Doctor_name }));
    setDoctor(mappedDoctors);
    const dCard = modeToCard(item.doctor_mode);
    setDoctorMode(dCard.cardMode); setIsDoctorActive(dCard.isActive || mappedDoctors.length > 0); setSelectAllDoctor(item.doctor_all ?? false);

    // Specialty
    const specialtyData = Array.isArray(item.specialty) ? item.specialty.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedSpecialty = specialtyData.map((o: any) => ({ value: o.id, label: o.id ? `(${o.id}) ${o.name}` : o.name }));
    setSpecialty(mappedSpecialty);
    const sCard = modeToCard(item.specialty_mode);
    setSpecialtyMode(sCard.cardMode); setIsSpecialtyActive(sCard.isActive || mappedSpecialty.length > 0); setSelectAllSpecialty(item.specialty_all ?? false);

    // Payer
    setPayer(item.payer || []);
    const payCard = modeToCard(item.payer_mode);
    setPayerMode(payCard.cardMode); setIsPayerActive(payCard.isActive || (item.payer?.length > 0)); setSelectAllPayer(item.payer_all ?? false);

    // User Level
    setUserLevel(item.user_level || []);
    const uCard = modeToCard(item.user_mode);
    setUserMode(uCard.cardMode); setIsUserActive(uCard.isActive || (item.user_level?.length > 0)); setSelectAllUser(item.user_all ?? false);

    setGender(item.gender);
    setCustomGroupName(item.relation_name);
    setIcds(mapToOptions(item.icd));
    const CustomIcdGroupsData = Array.isArray(item.custom_icd_groups) ? item.custom_icd_groups.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setCustomIcdGroups(CustomIcdGroupsData.map((o: any) => ({ value: o.id, label: o.name })));
    const MedicalIcdGroupsData = Array.isArray(item.medical_icd_groups) ? item.medical_icd_groups.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMedicalIcdGroups(MedicalIcdGroupsData.map((o: any) => ({ value: o.id, label: o.name })));

    setLowerAge(item.lower_age); setHigherAge(item.higher_age);
    setIsActiveToAge(item.is_active_to_age); setIsActiveToGender(item.is_active_to_gender ?? true);
    setIsActive(item.is_active !== false);

    const hasEntities = !!(
      item.provider?.length > 0 || item.doctor?.length > 0 || mappedSpecialty.length > 0 ||
      networksData.length > 0 || item.insuranceplan?.length > 0 || item.policy?.length > 0 ||
      PatientsData.length > 0 || item.vip?.length > 0 || item.payer?.length > 0 || item.user_level?.length > 0 ||
      (item.provider_mode && item.provider_mode !== "DISABLED") ||
      (item.network_mode && item.network_mode !== "DISABLED") ||
      (item.insuranceplan_mode && item.insuranceplan_mode !== "DISABLED") ||
      (item.payer_mode && item.payer_mode !== "DISABLED") ||
      (item.policy_mode && item.policy_mode !== "DISABLED") ||
      (item.patient_mode && item.patient_mode !== "DISABLED") ||
      (item.vip_mode && item.vip_mode !== "DISABLED") ||
      (item.doctor_mode && item.doctor_mode !== "DISABLED") ||
      (item.user_mode && item.user_mode !== "DISABLED") ||
      (item.specialty_mode && item.specialty_mode !== "DISABLED")
    );
    setShowEntities(hasEntities);

    if (item.icd && item.icd.length > 0) setSelectedMainOption("list_of_icd");
    else if (item.medical_icd_groups && item.medical_icd_groups.length > 0) setSelectedMainOption("medical_icd_groups");
    else if (item.custom_icd_groups && item.custom_icd_groups.length > 0) setSelectedMainOption("custom_icd_groups");
    else setSelectedMainOption(null);

    setEditIndex(item.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!customGroupName.trim()) { toast({ title: "Error", description: "Group Name is required and cannot be empty.", variant: "destructive" }); return; }
    if (customGroupName.trim().length < 3) { toast({ title: "Error", description: "Group Name must be at least 3 characters long.", variant: "destructive" }); return; }

    if (!isActiveToGender && !isActiveToAge) {
      toast({
        title: "Error",
        description: "At least one of Gender Status or Age Status must be Active. You cannot create a rule with both inactive.",
        variant: "destructive",
      });
      return;
    }

    if (isActiveToGender && !gender) {
      toast({
        title: "Error",
        description: "Gender Status is active — please choose a gender (Male or Female). No default is allowed.",
        variant: "destructive",
      });
      return;
    }

    if (isActiveToAge && (lowerAge == null || higherAge == null)) {
      toast({
        title: "Error",
        description: "Age Status is active — please enter both a lower and a higher age.",
        variant: "destructive",
      });
      return;
    }

    if (lowerAge != null && higherAge != null && Number(lowerAge) > Number(higherAge)) {
      toast({
        title: "Error",
        description: "Lower age cannot be greater than higher age.",
        variant: "destructive",
      });
      return;
    }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const updatedItem = {
        gender, relation_name: customGroupName,
        icd: icds.map((o) => o.value),
        custom_icd_groups: customIcdGroups.map((o) => o.value),
        medical_icd_groups: medicalIcdGroups.map((o) => o.value),
        lower_age: lowerAge, higher_age: higherAge, age_allowed: true,
        provider: selectAllProviders ? [] : buildProviderValues(provider),
        provider_mode: cardToMode(providerMode),
        provider_all: selectAllProviders,
        network: selectAllNetworks ? [] : networks.map((o) => o.value),
        network_mode: cardToMode(networkMode),
        network_all: selectAllNetworks,
        insuranceplan: selectAllInsurance ? [] : insuranceplan.map((o) => o.value),
        insuranceplan_mode: cardToMode(insuranceMode),
        insuranceplan_all: selectAllInsurance,
        payer: selectAllPayer ? [] : Payer,
        payer_mode: cardToMode(payerMode),
        payer_all: selectAllPayer,
        patient: selectAllPatients ? [] : patients.map((o) => o.label),
        patient_mode: cardToMode(patientMode),
        patient_all: selectAllPatients,
        policy: selectAllPolicy ? [] : buildPolicyData(policy),
        policy_mode: cardToMode(policyMode),
        policy_all: selectAllPolicy,
        user_level: selectAllUser ? [] : userLevel,
        user_mode: cardToMode(userMode),
        user_all: selectAllUser,
        vip: selectAllVip ? [] : vip.map((o) => o.value),
        vip_mode: cardToMode(vipMode),
        vip_all: selectAllVip,
        doctor: selectAllDoctor ? [] : doctor.map(buildDoctorData),
        doctor_mode: cardToMode(doctorMode),
        doctor_all: selectAllDoctor,
        specialty: selectAllSpecialty ? [] : buildSpecialtyData(specialty),
        specialty_mode: cardToMode(specialtyMode),
        specialty_all: selectAllSpecialty,
        is_active_to_age: isActiveToAge,
        is_active_to_gender: isActiveToGender,
        is_active: isActive,
        id, token,
      };
      const response = await RelationAddAgeAndGenderAxiosInstance.put("updateage-gender", updatedItem);
      if (response.data.success) {
       await fetchItems();
  setCurrentPage(0);
  clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Relation ICD Add Age And Gender updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };
  const handleDownload = (item: Result) => {
    const rows = ["gender;relation_name;icd;custom_icd_groups;medical_icd_groups;lower_age;higher_age;provider;provider_mode;provider_all;network;network_mode;network_all;insuranceplan;insuranceplan_mode;insuranceplan_all;payer;payer_mode;payer_all;patient;patient_mode;patient_all;policy;policy_mode;policy_all;user_level;user_mode;user_all;doctor;doctor_mode;doctor_all;vip;vip_mode;vip_all;specialty;specialty_mode;specialty_all;is_active_to_age;is_active_to_gender"];
    const icdCodes = item.icd || [];
    const medicalGroups = item.medical_icd_groups || [];
    const customGroups = item.custom_icd_groups || [];
    const providers = item.provider || [];
    const networksList = (item as any).network ?? item.networks ?? [];
    const insuranceplanList = item.insuranceplan || [];
    const patientsList = (item as any).patient ?? item.patients ?? [];
    const policyList = item.policy || [];
    const userLevelList = item.user_level || [];
    const doctorList = item.doctor || [];
    const payerList = item.payer || [];
    const vipList = item.vip || [];
    const specialtyList = item.specialty || [];
    const maxLen = Math.max(icdCodes.length, medicalGroups.length, customGroups.length, providers.length, networksList.length, insuranceplanList.length, patientsList.length, policyList.length, userLevelList.length, doctorList.length, payerList.length, vipList.length, specialtyList.length, 1);
    for (let index = 0; index < maxLen; index++) {
      rows.push([
        item.gender || "", item.relation_name || "",
        index < icdCodes.length ? icdCodes[index] : "",
        index < customGroups.length ? customGroups[index].name || "" : "",
        index < medicalGroups.length ? medicalGroups[index].name || "" : "",
        item.lower_age || "", item.higher_age || "",
        index < providers.length ? `${providers[index].Provider_id || ""} - ${providers[index].Provider_name || ""}` : "",
        item.provider_mode ?? "DISABLED", item.provider_all ? "TRUE" : "FALSE",
        index < networksList.length ? `${networksList[index].id || ""} - ${networksList[index].network_name || ""}` : "",
        item.network_mode ?? "DISABLED", item.network_all ? "TRUE" : "FALSE",
        index < insuranceplanList.length ? `${insuranceplanList[index].id || ""} - ${insuranceplanList[index].Plan_name || ""}` : "",
        item.insuranceplan_mode ?? "DISABLED", item.insuranceplan_all ? "TRUE" : "FALSE",
        index < payerList.length ? payerList[index] : "",
        item.payer_mode ?? "DISABLED", item.payer_all ? "TRUE" : "FALSE",
        index < patientsList.length ? `${patientsList[index].id || ""} - ${patientsList[index].name || ""}` : "",
        item.patient_mode ?? "DISABLED", item.patient_all ? "TRUE" : "FALSE",
        index < policyList.length ? policyList[index].Policy_name || "" : "",
        item.policy_mode ?? "DISABLED", item.policy_all ? "TRUE" : "FALSE",
        index < userLevelList.length ? userLevelList[index] : "",
        item.user_mode ?? "DISABLED", item.user_all ? "TRUE" : "FALSE",
        index < doctorList.length ? `${doctorList[index].Doctor_id || ""} - ${doctorList[index].Doctor_name || ""}` : "",
        item.doctor_mode ?? "DISABLED", item.doctor_all ? "TRUE" : "FALSE",
        index < vipList.length ? `${vipList[index].id || ""} - ${vipList[index].name || ""}` : "",
        item.vip_mode ?? "DISABLED", item.vip_all ? "TRUE" : "FALSE",
        index < specialtyList.length ? `${specialtyList[index].id || ""} - ${specialtyList[index].name || ""}` : "",
        item.specialty_mode ?? "DISABLED", item.specialty_all ? "TRUE" : "FALSE",
        item.is_active_to_age ? "TRUE" : "FALSE",
        item.is_active_to_gender ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relationAddAgeAndGender_${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatWithId = (id: any, name: any): string => {
    const idStr = id !== undefined && id !== null && id !== 0 && id !== "" ? `(${id}) ` : "";
    return `${idStr}${name || ""}`;
  };

  const toDisplayString = (val: any): string => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string" || typeof val === "number") return String(val);
    if (typeof val === "object") return val.name ?? val.label ?? val.value ?? val.id ?? JSON.stringify(val);
    return String(val);
  };

  // ─── Columns ──────────────────────────────────────────────────────────────
  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center font-normal" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
   cell: ({ row }) => (
  <div className="w-full flex justify-center font-medium">{row.index + 1}</div>
),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center font-normal" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
cell: ({ row }) => (
  <div className={`w-full text-center ${(row.original as any).is_deleted ? "line-through text-gray-400" : ""}`}>
    {row.getValue("relation_name")}
  </div>
),
    },
    {
      accessorKey: "icd",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center font-normal" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ICD {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const icdCodes = Array.isArray(item.icd) ? item.icd : [];
        const icdCustom = Array.isArray(item.custom_icd_groups) ? item.custom_icd_groups.map((g: any) => g.name || g) : [];
        const icdMedical = Array.isArray(item.medical_icd_groups) ? item.medical_icd_groups.map((g: any) => g.name || g) : [];
        const allDisplay = [
          ...icdCodes.map((c: any) => ({ label: String(c), kind: "icd" as const })),
          ...icdMedical.map((g: string) => ({ label: g, kind: "medical" as const })),
          ...icdCustom.map((g: string) => ({ label: g, kind: "custom" as const })),
        ];
        const total = allDisplay.length;
        if (total === 0) return <div className="w-full text-center text-gray-400">—</div>;

        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;
        const matchingDisplay = hasSearch
          ? allDisplay.filter((entry) =>
              entry.label.toLowerCase().includes(searchTerm),
            )
          : [];

        const hasMatchingDisplay = hasSearch && matchingDisplay.length > 0;
        const shown = hasMatchingDisplay
          ? matchingDisplay.slice(0, 3)
          : allDisplay.slice(0, 3);
        const dialogDisplay = hasMatchingDisplay ? matchingDisplay : allDisplay;
        const remainingCount = dialogDisplay.length - shown.length;

        const kindStyle = (
          kind: "icd" | "medical" | "custom",
          isMatching: boolean,
        ) => {
          if (isMatching) {
            return {
              bg: "#fef08a",
              color: "#713f12",
              border: "#facc15",
              tag: kind === "icd" ? "ICD" : kind === "medical" ? "MED" : "CUS",
            };
          }
          if (kind === "icd")     return { bg: "#011934", color: "#fff",    border: "#011934", tag: "ICD" };
          if (kind === "medical") return { bg: "#f0fdf4", color: "#15803d", border: "#86efac", tag: "MED" };
          return                         { bg: "#faf5ff", color: "#7e22ce", border: "#d8b4fe", tag: "CUS" };
        };

        return (
          <div className="w-full px-2 py-1.5">
            <div className="flex flex-wrap justify-center gap-1.5 mb-1">
              {shown.map((c, i) => {
                const isMatching =
                  hasSearch && c.label.toLowerCase().includes(searchTerm);
                const s = kindStyle(c.kind, isMatching);

                return (
                  <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 4px 2px 8px", borderRadius: 6, background: s.bg, border: `1px solid ${s.border}` }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: s.color }}>{c.label}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.2)", color: s.color, opacity: 0.85, border: `1px solid ${s.border}` }}>{s.tag}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 pt-1">
              <span className="text-xs font-bold text-gray-500">
                {hasMatchingDisplay
                  ? `${matchingDisplay.length}/${total}`
                  : total}
              </span>
              {remainingCount > 0 && (
                <button
                  onClick={() => {
                    setIcdCodesDialogList(dialogDisplay);
                    setIcdCodesDialogTitle(`${item.relation_name} – ICD Codes`);
                    setIcdCodesDialogOpen(true);
                  }}
                  className={`text-xs font-semibold ${
                    hasMatchingDisplay
                      ? "text-yellow-600 hover:text-yellow-800"
                      : "text-blue-600 hover:text-blue-800"
                  }`}
                >
                  +{remainingCount} more →
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "gender",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center font-normal" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Gender {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const gender = row.getValue("gender") as string;
        if (!gender) return <div className="w-full text-center" />;
        const normalized = gender.toLowerCase();
        const value = normalized === "male" ? "Male" : normalized === "female" || normalized === "feamal" ? "Female" : gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
        return <div className="w-full text-center">{value}</div>;
      },
    },
  {
      accessorKey: "is_active_to_gender",
      header: () => <div className="w-full text-center font-normal">Gender Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !(item as any).is_deleted && toggleActiveStatus(item, "gender")}
              disabled={!!(item as any).is_deleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${(item as any).is_deleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active_to_gender ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active_to_gender && !(item as any).is_deleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active_to_age",
      header: () => <div className="w-full text-center font-normal">Age Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !(item as any).is_deleted && toggleActiveStatus(item, "age")}
              disabled={!!(item as any).is_deleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${(item as any).is_deleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active_to_age ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active_to_age && !(item as any).is_deleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },

    {
      id: "view",
      header: () => <div className="w-full text-center font-normal">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => handleOpenDialog(row.original)} className="text-blue-600 hover:text-blue-800" title="View Details">
            <i className="fa-solid fa-eye text-lg"></i>
          </button>
        </div>
      ),
    },
  ];

  // ─── Toolbar dropdown styles ──────────────────────────────────────────────
  const ddBtn = (active: boolean, isGreen = false): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 7, padding: "0 16px", height: 38, borderRadius: 9999,
    border: active ? `1.5px solid ${isGreen ? "#10b981" : "#3b82f6"}` : "1.5px solid #e2e8f0",
    background: active ? isGreen ? "#f0fdf4" : "#eff6ff" : "#ffffff",
    cursor: "pointer", fontSize: 13, fontWeight: 500,
    color: active ? isGreen ? "#065f46" : "#1d4ed8" : "#64748b",
    whiteSpace: "nowrap" as const, transition: "border-color .15s, background .15s",
  });
  const ddMenu: React.CSSProperties = { position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 4, minWidth: 170, zIndex: 200, boxShadow: "0 6px 20px rgba(0,0,0,.10)" };
  const ddSectionLabel: React.CSSProperties = { fontSize: 11, color: "#94a3b8", padding: "5px 12px 3px", fontWeight: 600, textTransform: "capitalize", letterSpacing: ".5px" };
  const ddSeparator: React.CSSProperties = { height: 1, background: "#f1f5f9", margin: "3px 4px" };
  const ddItem = (selected: boolean, isGreen = false): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 9, padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontSize: 13,
    background: selected ? isGreen ? "#f0fdf4" : "#eff6ff" : "transparent",
    color: selected ? isGreen ? "#065f46" : "#1d4ed8" : "#1e293b",
    fontWeight: selected ? 500 : 400, transition: "background .1s",
  });
  const chevron = (open: boolean): React.CSSProperties => ({ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 });

  const toolbarExtras = (
    <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
        <button type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
          Add New Relation
        </button>
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, ICD, Medical Group, or Custom Group."
              />
        <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />

        <div style={{ position: "relative", flexShrink: 0 }}>
          <Input
            type="number"
            min={0}
            step={1}
            value={filterAge}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^\d+$/.test(value)) {
                setFilterAge(value);
                setCurrentPage(0);
              }
            }}
            placeholder="Age"
            aria-label="Filter by age"
            style={{ width: 92, height: 38, borderRadius: 9999 }}
          />
        </div>

        <div style={{ position: "relative", flexShrink: 0 }} ref={genderRef}>
          <button type="button" onClick={() => { setGenderOpen(o => !o); setGValidOpen(false); setAValidOpen(false); }} style={ddBtn(!!filterGender)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={filterGender ? "#3b82f6" : "#94a3b8"} strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
            {filterGender === "male" ? "Male" : filterGender === "female" ? "Female" : "Gender"}
            <svg style={chevron(genderOpen)} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          {genderOpen && (<div style={ddMenu}><div style={ddSectionLabel}>Gender</div>{[{ val: "", label: "All", color: "#94a3b8" }, { val: "male", label: "Male", color: "#3b82f6" }, { val: "female", label: "Female", color: "#ec4899" }].map((opt, i) => (<React.Fragment key={opt.val}>{i === 1 && <div style={ddSeparator} />}<div onClick={() => { setFilterGender(opt.val); setGenderOpen(false); }} style={ddItem(filterGender === opt.val)}><span style={{ width: 9, height: 9, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />{opt.label}</div></React.Fragment>))}</div>)}
        </div>

        <div style={{ position: "relative", flexShrink: 0 }} ref={gValidRef}>
          <button type="button" onClick={() => { setGValidOpen(o => !o); setGenderOpen(false); setAValidOpen(false); }} style={ddBtn(!!filterGenderValidation, true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={filterGenderValidation ? "#10b981" : "#94a3b8"} strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 3" /></svg>
            {filterGenderValidation === "true" ? "Active" : filterGenderValidation === "false" ? "Inactive" : "G. Status"}
            <svg style={chevron(gValidOpen)} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          {gValidOpen && (<div style={ddMenu}><div style={ddSectionLabel}>Gender Status</div>{[{ val: "", label: "All", color: "#94a3b8" }, { val: "true", label: "Active", color: "#10b981" }, { val: "false", label: "Inactive", color: "#ef4444" }].map((opt, i) => (<React.Fragment key={opt.val}>{i === 1 && <div style={ddSeparator} />}<div onClick={() => { setFilterGenderValidation(opt.val); setGValidOpen(false); }} style={ddItem(filterGenderValidation === opt.val, true)}><span style={{ width: 9, height: 9, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />{opt.label}</div></React.Fragment>))}</div>)}
        </div>

        <div style={{ position: "relative", flexShrink: 0 }} ref={aValidRef}>
          <button type="button" onClick={() => { setAValidOpen(o => !o); setGenderOpen(false); setGValidOpen(false); }} style={ddBtn(!!filterAgeValidation, true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={filterAgeValidation ? "#10b981" : "#94a3b8"} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
            {filterAgeValidation === "true" ? "Active" : filterAgeValidation === "false" ? "Inactive" : "A. Status"}
            <svg style={chevron(aValidOpen)} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          {aValidOpen && (<div style={ddMenu}><div style={ddSectionLabel}>Age Status</div>{[{ val: "", label: "All", color: "#94a3b8" }, { val: "true", label: "Active", color: "#10b981" }, { val: "false", label: "Inactive", color: "#ef4444" }].map((opt, i) => (<React.Fragment key={opt.val}>{i === 1 && <div style={ddSeparator} />}<div onClick={() => { setFilterAgeValidation(opt.val); setAValidOpen(false); }} style={ddItem(filterAgeValidation === opt.val, true)}><span style={{ width: 9, height: 9, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />{opt.label}</div></React.Fragment>))}</div>)}
        </div>

      </div>
      <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
        <FileUpload fetchItems={fetchItems} />
      </div>
    </div>
  );
  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-users"></i>
          </span>
          ICD Age and Gender
        </h5>
      </div>

      <DataTable2
        columns={columns}
        data={filteredData}
        searchColumns={[]}
        showAddButton={false}
        addButtonLabel="+ Add New Relation"
        onEdit={handleEdit}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
onDelete={(id) => {
  const item = items.result.find(i => i.id === id);
  if ((item as any)?.is_deleted) {
    setForceId(id); setForceAction("hardDelete"); setForceError(""); setForceDialogOpen(true);
  } else {
    removeItem(id);
  }
}}
onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
isRowDeleted={(row: any) => row.is_deleted === true}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        toolbarExtras={toolbarExtras}
      />

      {/* ===== Add / Edit Dialog ===== */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl h-[92vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex !== null ? "Edit ICD Age and Gender" : "Add ICD Age and Gender"}
              </DialogTitle> 
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic information" subtitle="Name and overall relation status">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Relation name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input value={customGroupName} onChange={(e) => setCustomGroupName(e.target.value)} placeholder="Enter a name for this relation" className="mt-1" />
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="Gender and age criteria" subtitle="Define who this rule applies to">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="mb-2 block">Gender <span className="text-red-500 ml-0.5">*</span></Label>
                  <div className="flex gap-2">
                    {[
                      { val: "female", label: "Female", active: "border-pink-400 bg-pink-50 text-pink-700" },
                      { val: "male",   label: "Male",   active: "border-blue-500 bg-blue-50 text-blue-700" },
                    ].map(({ val, label, active }) => (
                      <button key={val} type="button" onClick={() => setGender(val)}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all ${gender === val ? active : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="mb-2 block">Age range (years)</Label>
                  <div className="flex items-center gap-3">
                    <Input type="number" value={lowerAge ?? ""} onChange={(e) => setLowerAge(e.target.value === "" ? null : Number(e.target.value))} placeholder="From" min={0} max={120} className="flex-1" />
                    <span className="text-gray-400 text-lg font-light flex-shrink-0">—</span>
                    <Input type="number" value={higherAge ?? ""} onChange={(e) => setHigherAge(e.target.value === "" ? null : Number(e.target.value))} placeholder="To" min={0} max={120} className="flex-1" />
                  </div>
                </div>
                <StatusToggle label="Gender Status" dotColor="#D4537E" value={isActiveToGender} onChange={setIsActiveToGender} />
                <StatusToggle label="Age Status"    dotColor="#378ADD" value={isActiveToAge}    onChange={setIsActiveToAge}    />
              </div>
            </SectionCard>

            {/* Section 3 */}
            <SectionCard num={3} title="ICD configuration" subtitle="Which ICD codes or groups this rule covers">
              <div className="space-y-3">
                <div>
                  <Label className="mb-1.5 block">ICD source</Label>
                  <Select value={selectedMainOption || undefined} onValueChange={(v) => setSelectedMainOption(v)}>
                    <SelectTrigger><SelectValue placeholder="Select an option…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list_of_icd">List of ICD codes</SelectItem>
                      <SelectItem value="medical_icd_groups">Medical ICD groups</SelectItem>
                      <SelectItem value="custom_icd_groups">Custom ICD groups</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedMainOption === "list_of_icd" && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <Listofmedicalcode onChange={setIcds} defaultValue={icds} clear={clearIcds} description={IcdDescription} />
                  </div>
                )}
                {selectedMainOption === "medical_icd_groups" && (
                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                    <ListofMedicalGroup onChange={setMedicalIcdGroups} defaultValue={medicalIcdGroups} clear={clearMedicalIcdGroups} />
                  </div>
                )}
                {selectedMainOption === "custom_icd_groups" && (
                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                    <ListofCustomIcd onChange={setCustomIcdGroups} defaultValue={customIcdGroups} clear={clearCustomIcdGroups} />
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Section 4 – Entities */}
            <div className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">4</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                    <p className="text-xs text-blue-600">Scope and visibility — who this rule applies to</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { if (showEntities) resetAllEntityStates(); setShowEntities(prev => !prev); }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {showEntities && (
                <div className="p-5 bg-blue-50">
                  <div className="flex gap-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg mb-4">
                    <svg className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <p className="text-xs text-blue-900 leading-relaxed">
                      <span className="font-semibold text-emerald-700">Inclusion</span> = apply only to selected items &nbsp;·&nbsp;
                      <span className="font-semibold text-red-700">Exclusion</span> = apply to everyone except selected items
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Provider */}
                    <EntityCard label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if (providerMode === "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                      onInclusion={() => { if (providerMode === "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                      onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllProviders} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Providers</span>
                      </label>
                      {!selectAllProviders && <ListOfProviderName onChange={setProvider} defaultValue={provider} clear={clearProvider} />}
                    </EntityCard>

                    {/* Doctor */}
                    <EntityCard label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if (doctorMode === "EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                      onInclusion={() => { if (doctorMode === "INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                      onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Doctors</span>
                      </label>
                      {!selectAllDoctor && <ListOfDoctorName onChange={setDoctor} defaultValue={doctor} clear={clearDoctor} />}
                    </EntityCard>

                    {/* Specialty */}
                    <EntityCard label="Specialty" icon="fa-user-doctor" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if (specialtyMode === "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                      onInclusion={() => { if (specialtyMode === "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                      onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Specialties</span>
                      </label>
                      {!selectAllSpecialty && <ListOfSpecialty onChange={(opts: Option[]) => setSpecialty(opts)} defaultValue={specialty} clear={clearSpecialty} />}
                    </EntityCard>

                    {/* Networks */}
                    <EntityCard label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if (networkMode === "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                      onInclusion={() => { if (networkMode === "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                      onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Networks</span>
                      </label>
                      {!selectAllNetworks && <ListOfNewNetworkName onChange={(opts: any) => setNetworks(Array.isArray(opts) ? opts : opts ? [opts] : [])} defaultValue={networks} clear={clearNetworks} />}
                    </EntityCard>

                    {/* Insurance Plan */}
                    <EntityCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if (insuranceMode === "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                      onInclusion={() => { if (insuranceMode === "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                      onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                      </label>
                      {!selectAllInsurance && <ListOfInsuranceplanName onChange={setInsuranceplan} defaultValue={insuranceplan} clear={clearInsuranceplan} />}
                    </EntityCard>

                    {/* Policy */}
                    <EntityCard label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if (policyMode === "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                      onInclusion={() => { if (policyMode === "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                      onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Policies</span>
                      </label>
                      {!selectAllPolicy && <PolicyNames onChange={(v: PolicyItem[]) => setPolicy(v)} defaultValue={policy} clear={clearPolicy} />}
                    </EntityCard>

                    {/* Patients */}
                    <EntityCard label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if (patientMode === "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                      onInclusion={() => { if (patientMode === "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                      onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPatients} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Patients</span>
                      </label>
                      {!selectAllPatients && <VipPatientSelector onChange={setPatients} defaultValue={patients} clear={clearPatient} />}
                    </EntityCard>

                    {/* VIP */}
                    <EntityCard label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if (vipMode === "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                      onInclusion={() => { if (vipMode === "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                      onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllVip} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" />
                        <span className="text-sm font-medium text-slate-600">Select All VIP</span>
                      </label>
                      {!selectAllVip && <Vip onChange={setVip} defaultValue={vip} clear={clearVip} />}
                    </EntityCard>

                    {/* Payer */}
                    <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if (payerMode === "EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                      onInclusion={() => { if (payerMode === "INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                      onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Payers</span>
                      </label>
                      {!selectAllPayer && <Input value={Payer.join(", ")} onChange={(e) => setPayer(e.target.value.split(",").map(s => s.trim()))} placeholder="Enter payer…" />}
                    </EntityCard>

                    {/* User Level */}
                    <EntityCard label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                      onExclusion={() => { if (userMode === "EXCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("EXCLUSION"); setIsUserActive(true); } }}
                      onInclusion={() => { if (userMode === "INCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("INCLUSION"); setIsUserActive(true); } }}
                      onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllUser} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" />
                        <span className="text-sm font-medium text-slate-600">Select All User Levels</span>
                      </label>
                      {!selectAllUser && <Input value={userLevel.join(", ")} onChange={(e) => setUserLevel(e.target.value.split(",").map(s => s.trim()))} placeholder="Enter user level…" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setIsDialogOpen(false); clear(); }}>Cancel</Button>
            <Button onClick={() => editIndex !== null ? updateItem(editIndex) : addItem()} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex !== null ? "Update Relation" : "Add Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Details (View) Dialog ===== */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-[900px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-id-badge" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
  <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedItem.relation_name}</h2>
  {(selectedItem as any).is_deleted && (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
      <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
    </span>
  )}
</div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Rule ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
{(() => {
  const genderInactive = !selectedItem.is_active_to_gender;
  const ageInactive = !selectedItem.is_active_to_age;

  const cards = [
    { icon: "fa-venus-mars",    label: "Gender",        value: selectedItem.gender ? selectedItem.gender.charAt(0).toUpperCase() + selectedItem.gender.slice(1).toLowerCase() : "—", accent: "#60a5fa", warning: genderInactive },
    { icon: "fa-calendar-days", label: "Age Range",     value: `${selectedItem.lower_age ?? 0} — ${selectedItem.higher_age ?? "∞"} yrs`, accent: "#a78bfa", warning: ageInactive },
    { icon: "fa-shield-halved", label: "Gender Status", value: selectedItem.is_active_to_gender ? "Active" : "Inactive", accent: selectedItem.is_active_to_gender ? "#4ade80" : "#f87171", warning: false },
    { icon: "fa-shield-halved", label: "Age Status",    value: selectedItem.is_active_to_age ? "Active" : "Inactive", accent: selectedItem.is_active_to_age ? "#4ade80" : "#f87171", warning: false },
  ];

  return cards.map((card, i) => (
    <div
      key={i}
      style={{
        background: card.warning ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.07)",
        backdropFilter: "blur(6px)",
        borderRadius: 12,
        padding: "12px 14px",
        border: card.warning ? "1px solid rgba(239,68,68,0.7)" : "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <i className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.warning ? "#f87171" : card.accent, opacity: 0.9 }} />
        <span style={{ fontSize: 10, color: card.warning ? "rgba(252,165,165,0.85)" : "rgba(147,197,253,0.7)", fontWeight: 500, letterSpacing: "0.03em" }}>
          {card.label}
        </span>
      </div>
      <span style={{ fontSize: 15, fontWeight: 700, color: card.warning ? "#fca5a5" : card.accent }}>
        {card.value}
      </span>
    </div>
  ));
})()}
                </div>
              </div>

              {/* Body */}
              <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
                <div style={{ padding: "6px 0 16px" }}>
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                        <span style={{ width: 6, height: 6, borderRadius: 2, background: "#3b82f6", flexShrink: 0 }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>ICD Codes</span>
                        <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto" }}>{(selectedItem.icd || []).length} items</span>
                      </div>
                      <TagsWrap items={selectedItem.icd || []} color="blue" />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: "#22c55e", flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Medical ICD Groups</span>
                        </div>
                        <TagsWrap items={(selectedItem.medical_icd_groups || []).map((g: any) => g.name || g)} color="green" />
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                          <span style={{ width: 6, height: 6, borderRadius: 2, background: "#a855f7", flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>Custom ICD Groups</span>
                        </div>
                        <TagsWrap items={(selectedItem.custom_icd_groups || []).map((g: any) => g.name || g)} color="purple" />
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* Target Scope */}
                  <div style={{ padding: "20px 28px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                        <i className="fa-solid fa-bullseye" style={{ fontSize: 12, color: "#d97706" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope & Visibility</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      {[
                        { label: "Providers",       icon: "fa-hospital",         color: "#3b82f6", items: (selectedItem.provider || []).map((p: any) => formatWithId(p.Provider_id, p.Provider_name)), mode: selectedItem.provider_mode, all: (selectedItem as any).provider_all },
                        { label: "Doctors",         icon: "fa-stethoscope",      color: "#06b6d4", items: (selectedItem.doctor || []).map((d: any) => formatWithId(d.Doctor_id, d.Doctor_name)), mode: selectedItem.doctor_mode, all: (selectedItem as any).doctor_all },
                        { label: "Specialty",       icon: "fa-user-doctor",      color: "#0ea5e9", items: (selectedItem.specialty || []).map((s: any) => formatWithId(s.id, s.name)), mode: (selectedItem as any).specialty_mode, all: (selectedItem as any).specialty_all },
                        { label: "Networks",        icon: "fa-network-wired",    color: "#8b5cf6", items: ((selectedItem as any).network ?? selectedItem.networks ?? []).map((n: any) => formatWithId(n.id, n.network_name)), mode: (selectedItem as any).network_mode, all: (selectedItem as any).network_all },
                        { label: "Insurance Plans", icon: "fa-shield-halved",    color: "#f59e0b", items: (selectedItem.insuranceplan || []).map((i: any) => formatWithId(i.id, i.Plan_name)), mode: (selectedItem as any).insuranceplan_mode, all: (selectedItem as any).insuranceplan_all },
                        { label: "Policies",        icon: "fa-file-lines",       color: "#10b981", items: (selectedItem.policy || []).map((p: any) => `${p.Policy_id || ""} - ${p.Policy_name || ""}`), mode: (selectedItem as any).policy_mode, all: (selectedItem as any).policy_all },
                        { label: "Patients",        icon: "fa-users",            color: "#ec4899", items: ((selectedItem as any).patient ?? selectedItem.patients ?? []).map((p: any) => formatWithId(p.id, p.name ?? toDisplayString(p))), mode: (selectedItem as any).patient_mode, all: (selectedItem as any).patient_all },
                        { label: "VIP",             icon: "fa-star",             color: "#eab308", items: (selectedItem.vip || []).map((v: any) => formatWithId(v.id, v.name)), mode: (selectedItem as any).vip_mode, all: (selectedItem as any).vip_all },
                        { label: "Payer",           icon: "fa-building-columns", color: "#6366f1", items: (selectedItem.payer || []).map((p: any) => toDisplayString(p)), mode: (selectedItem as any).payer_mode, all: (selectedItem as any).payer_all },
                        { label: "User Levels",     icon: "fa-user-gear",        color: "#64748b", items: (selectedItem.user_level || []).map((u: any) => toDisplayString(u)), mode: (selectedItem as any).user_mode, all: (selectedItem as any).user_all },
                      ].map((e) => {
                        const isExclude  = e.mode === "EXCLUDED";
                        const isDisabled = !e.mode || e.mode === "DISABLED";
                        return (
                          <div key={e.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" as const }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                <div style={{ width: 24, height: 24, borderRadius: 6, background: `${e.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <i className={`fa-solid ${e.icon}`} style={{ fontSize: 10, color: e.color }} />
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
                              </div>
                              {!isDisabled && (
                                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, background: isExclude ? "#fef2f2" : "#f0fdf4", color: isExclude ? "#dc2626" : "#15803d", border: `1px solid ${isExclude ? "#fecaca" : "#bbf7d0"}` }}>
                                  {isExclude ? "Exclusion" : "Inclusion"}
                                </span>
                              )}
                            </div>
                            {e.all
                              ? <span style={{ fontSize: 11, color: "#475569", fontStyle: "italic" }}>All {e.label}</span>
                              : e.items.length > 0
                                ? <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                                    {e.items.map((item: string, i: number) => (
                                      <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", fontWeight: 500 }}>{item}</span>
                                    ))}
                                  </div>
                                : <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>
                            }
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                  {/* Audit Trail */}
                  <div style={{ padding: "20px 28px 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                        <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                            {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                        </div>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                          <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).createdat)}</span>
                        </div>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                          <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).updatedat)}</span>
                        </div>
                      </div>
                {((selectedItem as any).updated_by?.length > 0) && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && (<span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>)}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{formatDate(entry.at)}</span>
        </div>
      ))}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button
            onClick={() => setShowAllUpdates(v => !v)}
            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
          >
            {showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                    </div>
                  </div>
                </div>
              </ScrollArea>

              {/* Footer */}
              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
                  Close Details
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ICD Codes Detail Dialog */}
      <Dialog open={icdCodesDialogOpen} onOpenChange={setIcdCodesDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fa-solid fa-stethoscope text-rose-500" />
              {icdCodesDialogTitle}
              <span className="ml-2 text-sm font-normal text-gray-400">({icdCodesDialogList.length} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {icdCodesDialogList.filter(x => x.kind === "icd").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#011934", display: "inline-block" }} />
                  <span className="text-xs font-bold text-gray-600 tracking-wide">ICD Codes ({icdCodesDialogList.filter(x => x.kind === "icd").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {icdCodesDialogList.filter(x => x.kind === "icd").map((c, i) => (
                    <span key={i} style={{ padding: "4px 12px", background: "#011934", color: "white", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{c.label}</span>
                  ))}
                </div>
              </div>
            )}
            {icdCodesDialogList.filter(x => x.kind === "medical").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#16a34a", display: "inline-block" }} />
                  <span className="text-xs font-bold text-green-700 tracking-wide">Medical ICD Groups ({icdCodesDialogList.filter(x => x.kind === "medical").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {icdCodesDialogList.filter(x => x.kind === "medical").map((c, i) => (
                    <span key={i} style={{ padding: "4px 12px", background: "#f0fdf4", color: "#15803d", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #86efac" }}>{c.label}</span>
                  ))}
                </div>
              </div>
            )}
            {icdCodesDialogList.filter(x => x.kind === "custom").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#9333ea", display: "inline-block" }} />
                  <span className="text-xs font-bold text-purple-700 tracking-wide">Custom ICD Groups ({icdCodesDialogList.filter(x => x.kind === "custom").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {icdCodesDialogList.filter(x => x.kind === "custom").map((c, i) => (
                    <span key={i} style={{ padding: "4px 12px", background: "#faf5ff", color: "#7e22ce", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #d8b4fe" }}>{c.label}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button onClick={() => setIcdCodesDialogOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
{forceDialogOpen && (
  <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
    <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
        </div>

        {forceAction === "hardDelete" && (
          <>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
              This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the record.<br />This action <strong>cannot be undone</strong>.
            </p>
            {forceError && (
              <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                  <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
              <button
                onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Cancel
              </button>
              <button
                onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
              </button>
            </div>
          </>
        )}

        {forceAction === "softDelete" && (
          <>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Record Is In Use</h3>
            <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
              </div>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
              Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?
            </p>
            <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
              <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button onClick={() => { if (forceId) forceSoftDelete(forceId); }}
                style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                <i className="fa-solid fa-bolt mr-2"></i>Force Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
)}
      <Toaster />
    </div>
  );
};

export default IcdAddAgeAndGender;